import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Gauge, Calculator, ListChecks, CalendarBlank, AddressBook,
  Truck, Users, GearSix, SignOut, List, X, ShieldCheck, ChartLineUp,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, end: true },
  { to: "/quote", label: "Quote Calculator", icon: Calculator },
  { to: "/quotes", label: "Quotes & History", icon: ListChecks },
  { to: "/calendar", label: "Trip Calendar", icon: CalendarBlank },
  { to: "/contacts", label: "Contacts", icon: AddressBook },
  { to: "/vehicles", label: "Vehicles", icon: Truck, admin: true },
  { to: "/reports", label: "Reports", icon: ChartLineUp, admin: true },
  { to: "/users", label: "Users", icon: Users, admin: true },
  { to: "/settings", label: "Settings", icon: GearSix, admin: true },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const items = NAV.filter((n) => !n.admin || isAdmin);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-4 h-16 flex items-center border-b border-slate-800">
        <div className="bg-white rounded-md px-3 py-1.5 inline-flex items-center">
          <img src="/cargodash-logo.png" alt="CargoDash" className="h-6 w-auto" data-testid="brand-logo" />
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto cd-scroll">
        {items.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                isActive ? "bg-white text-slate-900" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`}
          >
            <n.icon size={18} weight="bold" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-heading font-semibold">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 flex items-center gap-1">
              {isAdmin && <ShieldCheck size={12} weight="fill" />}{user?.role}
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="text-slate-400 hover:text-white transition-colors" title="Sign out">
            <SignOut size={20} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col fixed inset-y-0 z-30">{SidebarInner}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-slate-900">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <button className="lg:hidden text-slate-700" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
          <div className="text-sm text-slate-500 hidden sm:block">
            {new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <div className="text-xs font-mono text-slate-400 ml-auto">Africa/Johannesburg · ZAR</div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full">{children}</main>
      </div>
    </div>
  );
}
